import KanbanStagesAPI from 'dashboard/api/kanbanStages';
import KanbanPipelinesAPI from 'dashboard/api/kanbanPipelines';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

export const useKanbanStagesStore = createStore({
  name: 'kanbanStages',
  type: 'pinia',

  state: () => ({
    conversationsByStage: {},
    // Per column: two columns can load at once, and a column that is loading must not be asked
    // for the same offset again.
    loadingMoreStageIds: [],
  }),

  getters: {
    getStagesByPipeline: state => pipelineId =>
      state.records
        .filter(
          stage => Number(stage.kanban_pipeline_id) === Number(pipelineId)
        )
        .sort((a, b) => a.position - b.position),

    getConversationsForStage: state => stageId =>
      state.conversationsByStage[stageId] || [],
  },

  actions: () => ({
    // One request for the whole board: stages, totals and the first page of every column.
    async getBoard(pipelineId) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { stages },
        } = await KanbanPipelinesAPI.getBoard(pipelineId);
        this.records = [
          ...this.records.filter(
            stage => Number(stage.kanban_pipeline_id) !== Number(pipelineId)
          ),
          ...stages.map(({ conversations, ...stage }) => stage),
        ];
        this.conversationsByStage = {
          ...this.conversationsByStage,
          ...Object.fromEntries(stages.map(s => [s.id, s.conversations])),
        };
        return stages;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async get(pipelineId) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await KanbanStagesAPI.get(pipelineId);
        this.records = [
          ...this.records.filter(
            stage => Number(stage.kanban_pipeline_id) !== Number(pipelineId)
          ),
          ...data,
        ];
        return data;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async create({ pipelineId, ...stageObj }) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await KanbanStagesAPI.create({
          pipelineId,
          ...stageObj,
        });
        this.records.push(data);
        return data;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ pipelineId, id, ...stageObj }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const { data } = await KanbanStagesAPI.update({
          pipelineId,
          id,
          ...stageObj,
        });
        const index = this.records.findIndex(stage => stage.id === data.id);
        if (index !== -1) this.records[index] = data;
        return data;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete({ pipelineId, id }) {
      this.setUIFlag({ deletingItem: true });
      try {
        await KanbanStagesAPI.delete({ pipelineId, id });
        this.records = this.records.filter(stage => stage.id !== id);
        delete this.conversationsByStage[id];
        return id;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async reorder({ pipelineId, positionsHash }) {
      try {
        await KanbanStagesAPI.reorder({ pipelineId, positionsHash });
        Object.entries(positionsHash).forEach(([id, position]) => {
          const stage = this.records.find(s => s.id === Number(id));
          if (stage) stage.position = position;
        });
      } catch (error) {
        throwErrorMessage(error);
      }
    },

    async getConversations({ pipelineId, id, offset = 0 }) {
      try {
        const {
          data: { payload },
        } = await KanbanStagesAPI.getConversations({ pipelineId, id, offset });
        this.conversationsByStage = {
          ...this.conversationsByStage,
          [id]:
            offset === 0
              ? payload
              : [...(this.conversationsByStage[id] || []), ...payload],
        };
        return payload;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async loadMoreConversations({ pipelineId, id }) {
      if (this.loadingMoreStageIds.includes(id)) return null;

      this.loadingMoreStageIds = [...this.loadingMoreStageIds, id];
      try {
        // Offset from what the column actually holds rather than a page number: an
        // optimistic drag changes how many server rows sit before the next card, so
        // page 2 would hand back a card already on screen (or skip one).
        return await this.getConversations({
          pipelineId,
          id,
          offset: (this.conversationsByStage[id] || []).length,
        });
      } finally {
        this.loadingMoreStageIds = this.loadingMoreStageIds.filter(
          stageId => stageId !== id
        );
      }
    },

    async assignConversationToStage({ conversation, stageId }) {
      return this.assignConversationsToStage({
        conversations: [conversation],
        stageId,
      });
    },

    async assignConversationsToStage({ conversations, stageId }) {
      const results = await Promise.allSettled(
        conversations.map(conversation =>
          ConversationAPI.toggleKanbanStage({
            conversationId: conversation.id,
            kanbanStageId: stageId,
          })
        )
      );
      const failedCount = results.filter(r => r.status === 'rejected').length;

      // Refetch the whole board, not just the target column: a conversation added from the
      // dialog may have been sitting in another column, which would otherwise keep showing it
      // and offset its next "load more" from a count that no longer matches the server. The
      // refetch also gives the cards the full shape (assignee, status, value) a search result
      // doesn't carry.
      const stage = this.records.find(s => s.id === Number(stageId));
      await this.getBoard(stage?.kanban_pipeline_id);

      if (failedCount) {
        return throwErrorMessage(
          new Error(`${failedCount} conversation(s) failed to be added`)
        );
      }
      return conversations;
    },

    async moveConversationToStage({ conversation, fromStageId, toStageId }) {
      // Optimistic move so the drag feels instant; reverted if the request fails.
      // The card's own stage id moves with it: a revert that left it pointing at the
      // target stage would make the next drag report the wrong source column.
      const fromList = this.conversationsByStage[fromStageId] || [];
      const toList = this.conversationsByStage[toStageId] || [];
      conversation.kanban_stage_id = toStageId;
      this.conversationsByStage = {
        ...this.conversationsByStage,
        [fromStageId]: fromList.filter(c => c.id !== conversation.id),
        [toStageId]: [conversation, ...toList],
      };

      const fromStage = this.records.find(s => s.id === Number(fromStageId));
      const toStage = this.records.find(s => s.id === Number(toStageId));
      if (fromStage) {
        fromStage.conversations_count -= 1;
        fromStage.total_value -= Number(conversation.value || 0);
      }
      if (toStage) {
        toStage.conversations_count += 1;
        toStage.total_value += Number(conversation.value || 0);
      }

      try {
        await ConversationAPI.toggleKanbanStage({
          conversationId: conversation.id,
          kanbanStageId: toStageId,
        });
      } catch (error) {
        // Undo this card only, on the columns as they are now: a page loaded or another card
        // moved while the request was out must survive the revert.
        const current = this.conversationsByStage;
        conversation.kanban_stage_id = fromStageId;
        this.conversationsByStage = {
          ...current,
          [fromStageId]: [
            conversation,
            ...(current[fromStageId] || []).filter(
              c => c.id !== conversation.id
            ),
          ],
          [toStageId]: (current[toStageId] || []).filter(
            c => c.id !== conversation.id
          ),
        };
        if (fromStage) {
          fromStage.conversations_count += 1;
          fromStage.total_value += Number(conversation.value || 0);
        }
        if (toStage) {
          toStage.conversations_count -= 1;
          toStage.total_value -= Number(conversation.value || 0);
        }
        throwErrorMessage(error);
      }
    },
  }),
});
